import React, { useState, useEffect } from "react";
import ChangeAddress from "../ChangeAddress/ChangeAddress";
import LogoWrapper from "../LogoWrapper/LogoWrapper";
import { Helmet } from "react-helmet-async";
import BackButton from "../BackButton/BackButton";
import { useFont } from "../../contexts/FontContext/FontContext";
import { startEllipsisInterval, getCarrier } from "../utils/common";
import usePortalSession from "../../hooks/usePortalSession";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./PurchaseLabelOption.module.css";

const PurchaseLabelOption = ( { logoUrl, itemQuantity } ) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const fontSchemes = useFont();
    const fontFamilyOne = fontSchemes.scheme_one;
    const [customerAddress, setCustomerAddress] = useState(null); //Need actual customer data from dan
    const [confirmLabelPurchase, setConfirmLabelPurchase] = useState(false);
    const [editAddress, setEditAddress] = useState(false);
    const [labelCost, setLabelCost] = useState(logoUrl == "triona_logo.png" ? "12" : "loading"); //hardcoded triona fixed rate for now
    const [carrierName, setCarrierName] = useState(null);
    const [dynamicText, setDynamicText] = useState("searching");
    const [isAnimating, setIsAnimating] = useState(true);
    const [shouldRefetch, setShouldRefetch] = useState(false); ///refetch rates if address is changed
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");
    const basePath = "/select_return_option/";
    const fullPath = location.pathname + location.search;
    const url = fullPath.startsWith(basePath) ? fullPath.slice(basePath.length) : fullPath;
    const sessionId = usePortalSession(token, apiUrl);

	//animated elipses loading effect
    useEffect(() => {
        if (isAnimating) {
            const interval = startEllipsisInterval(setDynamicText, dynamicText);
            return () => clearInterval(interval);
        }
    }, [isAnimating]);

    // Fetch customer address
    useEffect(() => {
        const fetchCustomerAddress = async () => {
            try {
                const response = await fetch(`${apiUrl}/api/get_customer_address/${token}/`, {
                    method: "GET",
                    headers: {"Content-Type": "application/json"}
                });
                if (!response.ok) throw new Error("Failed to fetch customer address.");
                const data = await response.json();
                setCustomerAddress(data.customer_address); // Update the state with the fetched address                
            } catch (error) { console.error("Error fetching customer address:", error); }
        };
        fetchCustomerAddress();
    }, [token]);

    useEffect(() => {
        const fetchRates = async () => {
            try {
            	if (!isAnimating) { setIsAnimating(true); }
                const response = await fetch(`${apiUrl}/api/create_shipment/${token}/`, {
                    method: "POST", 
                    headers: { 
                        "Content-Type": "application/json",
                        "X-Portal-Session": sessionId
                    } 
                });
                if (response.status === 409) {
                    alert("This portal was taken over in another window.");
                    navigate("/");
                    return;
                }
                if (!response.ok) throw new Error("Failed to fetch the shipment rates.");
                const data = await response.json();
                let selectedMargin = data.selected_margin; //brand's markup
                let { rate, carrier, service } = data.selected_rate;
                rate = parseFloat(rate);
                selectedMargin = parseFloat(selectedMargin);
                const shipwheelMargin = 1.00; //shipwheel's markup
                let labelSalePrice = Math.ceil(rate + shipwheelMargin + selectedMargin).toFixed(2); //brand keeps what they pick in margin tool
                if (logoUrl === "triona_logo.png") { labelSalePrice = 12.00; } //hardcoded for now. will add feature if other companies want fixed rate pricing
                setLabelCost(labelSalePrice);
                //const total_cost = itemQuantity === 1 ? 8 : itemQuantity === 2 ? 10 : 12; //original hardcoded fulton default. Rate isn't used in this case bc it is just based on item quantity
				//const markup = parseFloat((rate * 0.5).toFixed(2)); //markup defaults to 50% of best rate
				//const total_cost = (rate + markup).toFixed(2);
                //setLabelCost(total_cost);
                setCarrierName(getCarrier(carrier));
                setIsAnimating(false);
            } catch (error) {
                console.error("Error fetching shipment rates:", error);
                alert("Error fetching shipping label. Make sure address is valid!");
                setDynamicText("loading");
                setIsAnimating(false);
            }
        };
        if (shouldRefetch) {
	        fetchRates();
	        setShouldRefetch(false); 
	    }
        fetchRates();
    }, [shouldRefetch, token, customerAddress]);

    const handleSaveAddress = async (newAddress) => {
	    try {
	        const response = await fetch(`${apiUrl}/api/update_customer_address/${token}/`, {
	            method: "PUT",
	            headers: { "Content-Type": "application/json" },
	            body: JSON.stringify({ customer_address: newAddress, ship_to_address: null }),
	        });
	        if (!response.ok) throw new Error("Failed to update customer address.");	        
	        // Only update state after the API request is complete
	        setCustomerAddress(newAddress);
	        setEditAddress(false);
	        setShouldRefetch(true);
            setDynamicText("searching");
	    } catch (error) { console.error("Error updating customer address:", error); }
	};

    //purchase label and provide user with return tracking link and label download url
    const purchaseLabel = async () => {
    	setConfirmLabelPurchase(true);
        setDynamicText("Downloading label");
    	setIsAnimating(true);
        try {
            const response = await fetch(`${apiUrl}/api/purchase_shipping_label/${token}/`, { 
            	method: "POST", 
            	headers: { 
                    "Content-Type": "application/json",
                    "X-Portal-Session": sessionId
                },
            	body: JSON.stringify({ label_purchase_price: labelCost })
            });
            if (response.status === 409) {
                alert("This portal was taken over in another window.");
                navigate("/");
                return;
            }
            if (!response.ok) { throw new Error("Failed to purchase the label."); }
            const data = await response.json();
            const label_url = data.label_url;
            window.open(label_url, "_blank");
            navigate(`/returns_confirm/${url}`);
        } catch (error) { alert("There was an error downloading your label. Please ensure your address is correct."); }
    };

    return (
        <div className={`${styles.purchase_label_option_container} ${fontFamilyOne}`}>
            <Helmet><title>Purchase label</title></Helmet>
            <LogoWrapper class_={styles.purchase_label_option_logo} src={logoUrl ? `${process.env.PUBLIC_URL}/business_images/${logoUrl}` : `${process.env.PUBLIC_URL}/images/partial_logo.png`} />
            <p className={`titles ${styles.purchase_label_title} fade_in`}>Download return label</p>
            <div className={`${styles.purchase_label_info_container} fade_in`}>
                {!editAddress && !confirmLabelPurchase && (
                    <>
                        <div className={styles.carrier_container}>
                        	<p className={`${styles.carrier_label}`}>Carrier: </p>
                        	<img src={`${process.env.PUBLIC_URL}/images/${carrierName && !isAnimating ? `${carrierName.toLowerCase()}_logo.png` : "mail_truck.gif"}`} alt="carrier_logo" className={styles.carrier_logo} />
                        </div>
                        <div className={styles.from_address_container}><p className={`${styles.shipping_from_label}`}>Shipping from: </p><p className={styles.from_address}>{customerAddress}</p></div>
                        <div className={`${styles.edit_address_button} login_buttons`} onClick={() => setEditAddress(true)}>Edit address</div>
                        <div className={`${styles.label_cost_container}`}><p className={styles.label_cost}>{logoUrl == "triona_logo.png" ? "Label cost" : "Best rate"}: {isAnimating && logoUrl !== "triona_logo.png" ? dynamicText : `$${labelCost}`}</p><p className={styles.deduction_blurb}>( Automatically deducted from your refund. )</p></div>
                        <button className={`login_buttons ${styles.purchase_label_button} ${fontFamilyOne}`} disabled={isAnimating} style={isAnimating ? { opacity: 0.5 } : {}} onClick={purchaseLabel}>Download</button> {/* need button bc disabled doesnt work on div */}
                    </>
                )}
                {editAddress && (<ChangeAddress setNewAddress={handleSaveAddress} />)}
                {confirmLabelPurchase && (
                    <div className={styles.confirm_purchase_container}>
                    	<img src={`${process.env.PUBLIC_URL}/images/attach_return_label.png`} alt="attach_return_label" className={styles.attach_return_label_image} />
                        <p className={`${styles.confirming_text}`}>{dynamicText}</p>
                    </div>
                )}
            </div>
            <p className={`titles ${styles.attach_to_parcel}`}>Attach this return label to your parcel.</p>
            <BackButton button_id={styles.purchase_label_back_button} image_id={styles.purchase_label_back_button_arrow} />
        </div>
    );
};

export default PurchaseLabelOption;